import React, { useCallback, useEffect, useRef, useState } from "react";
import { useThreadStorageKey } from "./ThreadProvider.js";
import { useActivity } from "./ActivityProvider.js";
import { ui, type Lang } from "./shared.js";
import { cleanMarkdown, streamChat, type LocalMsg, type LocalFileCtx } from "./chatApi.js";
import { getFreshToken } from "./auth.js";
import { FolderActivityStream } from "./folder_activity_stream.js";

// ---------------------------------------------------------------------------
// ChatPanel (formerly SimpleLeftPanel)
// ---------------------------------------------------------------------------

const CHAT_MODELS_L1 = [
  { id: "gemini-pro",    label: "Gemini 2.5 Pro",    shortLabel: "GEMINI 2.5 PRO",   byok: false },
  { id: "gemini-flash",  label: "Gemini 2.5 Flash",  shortLabel: "GEMINI 2.5 FLASH", byok: false },
  { id: "claude-sonnet", label: "Claude Sonnet",      shortLabel: "CLAUDE SONNET",    byok: true  },
  { id: "kimi",          label: "Kimi 2.5 (中国区)",  shortLabel: "KIMI 2.5",         byok: true  },
  { id: "glm4",          label: "GLM-4 (中国区)",     shortLabel: "GLM-4",            byok: false },
  { id: "mistral",       label: "Mistral Large (EU)", shortLabel: "MISTRAL LARGE",    byok: false },
] as const;

interface LocalActivity { id: string; icon: string; text: string; ts: number; }

function fmtRelTimeL1(ts: number): string {
  const diff = Date.now() - ts;
  if (diff < 60_000) return "刚刚";
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}分钟前`;
  const d = new Date(ts);
  if (d.toDateString() === new Date().toDateString()) return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  return `${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")} ${d.toLocaleTimeString([], { hour:"2-digit", minute:"2-digit" })}`;
}

function fileIconL1(ft?: string, fn?: string): string {
  const n = (fn ?? "").toLowerCase();
  if ((ft ?? "").startsWith("image/") || /\.(png|jpe?g|webp|gif)$/.test(n)) return "🖼️";
  if (n.endsWith(".pdf")) return "📕";
  if (n.endsWith(".json")) return "📋";
  if (/\.xlsx?$/.test(n)) return "📊";
  return "📎";
}

export function ChatPanel({ lang, activeFolderId }: { lang: Lang; activeFolderId?: string | null }) {
  const storageKey = useThreadStorageKey();
  const { emit: _emitAct } = useActivity();
  const [tab,        setTab]       = useState<"chat"|"activity">("chat");
  const [messages,   setMessages]  = useState<LocalMsg[]>(() => {
    try {
      const raw = localStorage.getItem("studio_chat_landing");
      return raw ? (JSON.parse(raw) as LocalMsg[]) : [];
    } catch { return []; }
  });
  const [actLog,     setActLog]    = useState<LocalActivity[]>([]);
  const [draft,      setDraft]     = useState("");
  const [streaming,  setStreaming] = useState(false);
  const [streamText, setStreamTxt] = useState("");
  const [error,      setError]     = useState<string|null>(null);
  const [fileCtx,    setFileCtx]   = useState<LocalFileCtx|null>(null);
  const [chatModel,  setChatModel] = useState<string>(() => localStorage.getItem("studio_chat_model") ?? "gemini-pro");
  const bottomRef    = useRef<HTMLDivElement>(null);
  const actBottomRef = useRef<HTMLDivElement>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const abortRef     = useRef<AbortController|null>(null);
  const storageKeyRef = useRef(storageKey);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior:"smooth" }); }, [messages.length, streamText]);
  useEffect(() => { actBottomRef.current?.scrollIntoView({ behavior:"smooth" }); }, [actLog.length]);
  useEffect(() => () => { abortRef.current?.abort(); }, []);

  useEffect(() => {
    storageKeyRef.current = storageKey;
    try {
      const raw = localStorage.getItem(storageKey);
      setMessages(raw ? (JSON.parse(raw) as LocalMsg[]) : []);
    } catch { setMessages([]); }
  }, [storageKey]);

  useEffect(() => {
    if (messages.length === 0) return;
    if (storageKeyRef.current !== storageKey) return;
    try {
      const toSave = messages.slice(-120).map(m => ({ id: m.id, role: m.role, text: m.text, ts: m.ts, fileName: m.fileName, fileType: m.fileType, imgSrc: m.imgSrc }));
      localStorage.setItem(storageKey, JSON.stringify(toSave));
    } catch { /* ignore localStorage errors */ }
  }, [messages, storageKey]);

  const addAct = useCallback((entry: Omit<LocalActivity,"id">) => {
    setActLog(prev => [...prev.slice(-199), { ...entry, id: `${Date.now()}-${Math.random().toString(36).slice(2,6)}` }]);
    _emitAct(
      "FIELD_UPDATED" as import("../../../shared/db/types.js").ActivityEventType,
      entry.text.slice(0, 120),
      { icon: entry.icon } as Record<string, unknown>,
    );
  }, [_emitAct]);

  const sendToAI = useCallback(async (userText: string) => {
    abortRef.current?.abort();
    const controller = new AbortController();
    abortRef.current = controller;
    setStreaming(true); setStreamTxt(""); setError(null);
    try {
      const result = await streamChat(userText, messages, chatModel, fileCtx, controller.signal, setStreamTxt);
      if (result.error) setError(result.error);
      if (result.display) setMessages(prev => [...prev, { id:`ai-${Date.now()}`, role:"ai", text:result.display as string, ts:Date.now() }]);
      if (result.fieldUpdates) {
        window.dispatchEvent(new CustomEvent("studio:fieldUpdates", {
          detail: { updates: result.fieldUpdates },
        }));
      }
    } finally { setStreaming(false); setStreamTxt(""); }
  }, [messages, chatModel, fileCtx]);

  const send = useCallback(() => {
    const text = draft.trim();
    if (!text || streaming) return;
    setMessages(prev => [...prev, { id:`u-${Date.now()}`, role:"user", text, ts:Date.now() }]);
    setDraft("");
    void sendToAI(text);
  }, [draft, streaming, sendToAI]);

  const handleFile = useCallback(async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (fileInputRef.current) fileInputRef.current.value = "";
    const fileMsgId = `file-${Date.now()}`;
    const isImageFile = (file.type || "").startsWith("image/") || /\.(png|jpe?g|webp|gif)$/i.test(file.name);
    setMessages(prev => [...prev, { id: fileMsgId, role: "file", text: file.name, ts: Date.now(), fileName: file.name, fileType: file.type }]);
    addAct({ icon:"📎", text:`上传了 ${file.name}`, ts:Date.now() });
    const reader = new FileReader();
    reader.onload = async (ev) => {
      const dataUrl = ev.target?.result as string;
      if (isImageFile) {
        setMessages(prev => prev.map(m => m.id === fileMsgId ? { ...m, imgSrc: dataUrl } : m));
      }
      const base64Data = dataUrl.split(",")[1] ?? "";
      const fileType = file.type || (file.name.split(".").pop() ?? "binary");
      try {
        const uploadToken = await getFreshToken() ?? "";
        const uploadRes = await fetch("/api/upload", {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            ...(uploadToken ? { Authorization: `Bearer ${uploadToken}` } : {}),
          },
          body: JSON.stringify({ fileName: file.name, fileType, base64Data }),
        });
        if (uploadRes.ok) { const ctx = await uploadRes.json() as LocalFileCtx; setFileCtx(ctx); addAct({ icon:"📂", text:`${file.name} 已提取内容，AI可引用`, ts:Date.now() }); }
      } catch { /* silent */ }
    };
    reader.readAsDataURL(file);
  }, [addAct]);

  const activeModel = CHAT_MODELS_L1.find(m => m.id === chatModel) ?? CHAT_MODELS_L1[0];

  const SEND_BTN: React.CSSProperties = {
    background: (draft.trim() && !streaming) ? "#ffffff" : "transparent",
    border: "1px solid #ffffff", color: (draft.trim() && !streaming) ? "#000000" : "#777777",
    fontFamily: "'Rama Gothic E', system-ui", fontSize: "0.6rem", fontWeight: "normal",
    letterSpacing: "0.15em", padding: "0 20px", cursor: (draft.trim() && !streaming) ? "pointer" : "default",
    flexShrink: 0, whiteSpace: "nowrap" as const, textTransform: "uppercase" as const, height: 68,
  };
  const ICON_BTN: React.CSSProperties = { background: "transparent", border: "none", cursor: "pointer", color: "var(--text-muted)", fontSize: 14, padding: "0 4px", lineHeight: 1, flexShrink: 0, display: "flex", flexDirection: "column" as const, alignItems: "center", gap: 2 };

  return (
    <div style={{ display:"flex", flexDirection:"column", height:"100%", overflow:"hidden" }}>
      <div className="sf-tabs">
        <button className={`sf-tab${tab==="chat"?" active":""}`} onClick={() => setTab("chat")}>{ui("chat", lang)}</button>
        <button className={`sf-tab${tab==="activity"?" active":""}`} onClick={() => setTab("activity")}>{ui("activity", lang)}</button>
      </div>

      <div style={{ flex:1, overflowY:"auto", minHeight:0, padding:"12px" }}>
        {tab === "chat" ? (
          <>
            {messages.length === 0 && !streaming ? (
              <div className="sf-empty-state">{ui("no_messages", lang)}</div>
            ) : (
              <>
                {messages.map(msg => {
                  if (msg.role === "file") {
                    if (msg.imgSrc) {
                      return (
                        <div key={msg.id} style={{ marginBottom: 10 }}>
                          <img src={msg.imgSrc} alt={msg.fileName} style={{ maxWidth: "100%", maxHeight: 200, borderRadius: 4, display: "block", border: "1px solid #2a2a2a" }} />
                          <div style={{ fontFamily: "'Space Mono', monospace", fontSize: "0.55rem", color: "var(--text-muted)", marginTop: 3 }}>{msg.fileName}</div>
                        </div>
                      );
                    }
                    return (<div key={msg.id} className="sf-file-card" style={{ marginBottom:10 }}><span style={{ fontSize:16 }}>{fileIconL1(msg.fileType, msg.fileName)}</span><span className="sf-file-card-name">{msg.fileName ?? msg.text}</span></div>);
                  }
                  return (
                    <div key={msg.id} style={{ marginBottom:14 }}>
                      <div style={{ display:"flex", alignItems:"baseline", gap:8, marginBottom:4, justifyContent: msg.role==="ai" ? "flex-start" : "flex-end" }}>
                        <span style={{ fontFamily:"'Rama Gothic E', system-ui", fontSize:"0.6rem", fontWeight:"normal", letterSpacing:"0.2em", textTransform:"uppercase", color:"var(--text-muted)" }}>{msg.role==="ai" ? "AI" : "YOU"}</span>
                        <span style={{ fontSize:9, color:"#333333" }}>{new Date(msg.ts).toLocaleTimeString([],{hour:"2-digit",minute:"2-digit"})}</span>
                      </div>
                      <div style={{ fontFamily:"'Space Mono', monospace", fontSize:"0.78rem", color: msg.role==="ai" ? "var(--text)" : "var(--text-muted)", lineHeight:1.7, wordBreak:"break-word", textAlign: msg.role==="ai" ? "left" : "right" as const }}>{msg.role==="ai" ? cleanMarkdown(msg.text) : msg.text}</div>
                    </div>
                  );
                })}
                {streaming && (
                  <div style={{ marginBottom:14 }}>
                    <div style={{ display:"flex", alignItems:"baseline", gap:8, marginBottom:4 }}>
                      <span style={{ fontFamily:"'Rama Gothic E', system-ui", fontSize:"0.6rem", fontWeight:"normal", letterSpacing:"0.2em", textTransform:"uppercase", color:"var(--text-muted)" }}>AI</span>
                      <span style={{ fontSize:9, color:"#333333" }}>now</span>
                    </div>
                    <div style={{ fontFamily:"'Space Mono', monospace", fontSize:"0.78rem", color:"var(--text)", lineHeight:1.7, wordBreak:"break-word" }}>{streamText ? cleanMarkdown(streamText) : <span style={{ opacity:0.5 }}>▋</span>}</div>
                  </div>
                )}
              </>
            )}
            {error && <div style={{ fontFamily:"'Space Mono', monospace", fontSize:"0.78rem", color:"#e05555", padding:"6px 0" }}>⚠ {error}</div>}
            <div ref={bottomRef} />
          </>
        ) : (
          // Folder-service-level Activity Log (P2a-3/V1): the unified folder/team
          // stream for the active shared folder. Folder-level events (invite,
          // member, share/unshare, pack add/remove) belong here, NOT in the Tech
          // Pack editor's AwarenessPanel.
          <FolderActivityStream scope="folder" id={activeFolderId ?? null} lang={lang} />
        )}
      </div>

      <div className="sf-model-row">
        <div className="sf-model-sel-wrap">
          <span className="sf-engine-tag">STUDIO</span><span className="sf-model-sep">|</span>
          <span className={`sf-engine-badge${activeModel.id.startsWith("gemini") ? " gemini" : ""}`}>{activeModel.shortLabel}</span><span className="sf-model-sep">|</span>
          <select value={chatModel} onChange={e => { setChatModel(e.target.value); localStorage.setItem("studio_chat_model", e.target.value); }} className="sf-model-sel">
            {CHAT_MODELS_L1.map(m => <option key={m.id} value={m.id}>{m.label}{m.byok ? " (BYOK)" : ""}</option>)}
          </select>
        </div>
        <div className="sf-model-disclaimer">Output quality varies by model.</div>
      </div>

      <div style={{ display:"flex", alignItems:"stretch", gap:0, padding:"6px 12px 4px", flexShrink:0 }}>
        <input type="text" value={draft} onChange={e => setDraft(e.target.value)} onKeyDown={e => { if (e.key==="Enter" && !e.shiftKey) { e.preventDefault(); send(); } }} placeholder={streaming ? ui("ai_thinking", lang) : ui("msg_placeholder", lang)} disabled={streaming} className="sf-chat-input" style={{ flex:1 }} />
        <button onClick={send} disabled={!draft.trim() || streaming} style={SEND_BTN}>SEND</button>
      </div>

      <div style={{ display:"flex", alignItems:"center", gap:8, padding:"4px 12px 12px", flexShrink:0 }}>
        <input ref={fileInputRef} type="file" accept="image/*,application/pdf,application/json,.xlsx,.xls" style={{ display:"none" }} onChange={handleFile} />
        <button onClick={() => fileInputRef.current?.click()} title="Upload file" style={ICON_BTN}>
          <span>&#128206;</span>
          <span style={{ fontFamily:"'Rama Gothic E', system-ui", fontSize:"0.5rem", letterSpacing:"0.1em", textTransform:"uppercase", color:"var(--text-muted)" }}>UPLOAD</span>
        </button>
        <button disabled aria-label="Voice input unavailable" title="Voice input — coming soon" style={{ ...ICON_BTN, cursor:"not-allowed", color:"#8a8a8a", opacity:0.55 }}>
          <svg aria-hidden="true" width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"><rect x="9" y="2" width="6" height="12" rx="3"/><path d="M5 10a7 7 0 0 0 14 0M12 17v5m-4 0h8"/></svg>
          <span style={{ fontFamily:"'Rama Gothic E', system-ui", fontSize:"0.5rem", letterSpacing:"0.1em", textTransform:"uppercase", color:"#8a8a8a" }}>VOICE</span>
        </button>
        {fileCtx && <span style={{ fontFamily:"'Space Mono', monospace", fontSize:"0.6rem", color:"#4a4a6a", letterSpacing:"0.05em", overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", flex:1 }}>{fileIconL1(fileCtx.fileType, fileCtx.fileName)} {fileCtx.fileName}</span>}
      </div>
    </div>
  );
}
